import type { CmpConfig } from "./core/config";
import { fetchConfig, getCachedConfig, getUserLang } from "./core/config";
import { resolveCategories } from "./core/categories";
import { getConsent, setConsent, buildAcceptAllConsent, buildRejectAllConsent, buildDefaultConsent, buildOptOutConsent, type ConsentState } from "./core/consent";
import {
  clearConsent,
  getUserKey,
  ensureCmpUserKey,
  getStoredPolicyVersion,
  savePolicyVersion,
  getStoredBannerVersion,
  saveBannerVersion,
} from "./core/storage";
import { renderBanner, removeBanner, type BannerOptions } from "./ui/banner";
import {
  removeConsentWidget,
  setConsentWidgetOpenHandler,
  syncConsentWidget,
} from "./ui/consent-widget";
import { releasePageLockIfIdle, setStrictPageBlock, shouldBlockPageUntilConsent } from "./ui/page-lock";
import { renderPreferences, closePreferences } from "./ui/preferences";
import { activateScripts, retriggerScripts, getBlockingStats } from "./blocking/script-blocker";
import { installCookieWriteGuard, clearDeniedCookies, clearDeniedLocalStorage } from "./blocking/cookie-blocker";
import { setDefaultDenied, updateConsentMode } from "./consent-mode/gcm-v2";
import {
  detectPostLoginCookieMismatch,
  resolveLoginReconsentNotice,
} from "./core/post-login-consent";

export type CmpEventType = "cmp:ready" | "cmp:consentChanged";
type EventCallback = (data?: unknown) => void;

const listeners: Record<string, EventCallback[]> = {};
let config: CmpConfig | null = null;

// ── Event sending ────────────────────────────────────────────────

async function sendConsentEvent(eventType: string, consent: ConsentState): Promise<void> {
  if (!config) return;
  const apiBase = (window as unknown as { __CMP_API_BASE__?: string }).__CMP_API_BASE__ || "";

  // Legacy consent event (consent_events table)
  try {
    await fetch(`${apiBase}/cmp/consents`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        hostname: window.location.hostname,
        policyVersion: config.policyVersion,
        eventType,
        userKey: getUserKey(),
        consent,
      }),
    });
  } catch (err) {
    console.warn("[CMP] Failed to send consent event:", err);
  }

  // Granular consent record (consent_records table)
  try {
    await fetch(`${apiBase}/api/consent/save`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        hostname: window.location.hostname,
        visitorId: getUserKey(),
        consentPayload: {
          categories: consent.categories,
          cookies: consent.cookies,
          policy_version: config.policyVersion,
          action: eventType,
        },
      }),
    });
  } catch (err) {
    console.warn("[CMP] Failed to save consent record:", err);
  }
}

// ── Event emitter ────────────────────────────────────────────────

function emit(event: CmpEventType, data?: unknown): void {
  window.dispatchEvent(new CustomEvent(event, { detail: data }));
  for (const cb of listeners[event] || []) {
    try { cb(data); } catch {}
  }
}

/**
 * Müşteri sitesi login köprüsü.
 * `authToken`: CMP API’de yapılandırılan Visitor Link anahtarı (Bearer) — üretimde tarayıcıya ham kullanıcı JWT’si koymayın.
 * @see MERCHANT_LOGIN_BRIDGE.md
 */
export type LinkVisitorToUserOptions = {
  /** Authorization: Bearer … — zorunlu (sunucuya istek için). */
  authToken?: string;
  /** Varsayılan: `window.__CMP_API_BASE__` / `data-api-base`. */
  apiBase?: string;
  /** İstek gövdesindeki hostname; varsayılan `window.location.hostname`. */
  hostname?: string;
  /** true ise başarılı link sonrası çerez/rıza köprü kontrolü çalışmaz.  */
  skipPostLoginCookieCheck?: boolean;
};

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function parseVisitorGuid(visitorKey: string): string | null {
  const t = visitorKey.trim();
  if (UUID_RE.test(t)) return t.toLowerCase();
  return null;
}

/**
 * Rıza köprüsü: yerelde `cmp_user_key` sağlar, harici üye id ile public `/api/consent/link-visitor` çağrısı yapar.
 * Site akışını bozmamak için ağ/CORS hatalarında yalnızca `console.error`.
 *
 * ```js
 * // Müşteri login başarılı olduktan sonra (ör. koton.com):
 * window.CMP?.linkVisitorToUser?.("USER_123", { authToken: CMP_LINK_SITE_SECRET });
 * ```
 */
export async function linkVisitorToUser(
  externalUserId: string,
  options: LinkVisitorToUserOptions = {}
): Promise<{ ok: boolean }> {
  const visitorRaw = ensureCmpUserKey();
  const visitorId = parseVisitorGuid(visitorRaw);
  if (!visitorId) {
    console.debug("[CMP] linkVisitorToUser: cmp_user_key geçerli UUID değil", visitorRaw);
    return { ok: false };
  }

  const apiBase =
    options.apiBase ?? (window as unknown as { __CMP_API_BASE__?: string }).__CMP_API_BASE__ ?? "";
  const hostname = options.hostname ?? window.location.hostname;
  const token = options.authToken?.trim();

  if (!token) {
    return { ok: false };
  }

  try {
    const res = await fetch(`${apiBase.replace(/\/$/, "")}/api/consent/link-visitor`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        visitorId,
        externalUserId: String(externalUserId),
        hostname,
      }),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => res.statusText);
      console.debug("[CMP] linkVisitorToUser failed:", res.status, text);
      return { ok: false };
    }
    if (!options.skipPostLoginCookieCheck) {
      runPostLoginConsentBridge();
    }
    return { ok: true };
  } catch (e) {
    console.debug("[CMP] linkVisitorToUser error:", e);
    return { ok: false };
  }
}

// ── Core consent handler ─────────────────────────────────────────

async function handleConsent(consent: ConsentState, eventType: string): Promise<void> {
  try {
    setConsent(consent);
    updateConsentMode(consent.categories);

    if (config) {
      savePolicyVersion(config.policyVersion);
      saveBannerVersion(config.bannerVersion);
    }

    const newlyActivated = await retriggerScripts(consent.categories);
    clearDeniedCookies();

    // Clear localStorage for denied categories (analytics, marketing)
    const deniedCategories = Object.entries(consent.categories)
      .filter(([, v]) => !v)
      .map(([k]) => k);
    clearDeniedLocalStorage(deniedCategories);

    if (newlyActivated > 0) {
      console.info(`[CMP] ${newlyActivated} new script(s) activated after consent change.`);
    }

    await sendConsentEvent(eventType, consent);
    emit("cmp:consentChanged", consent);
    syncConsentWidget(config);
  } catch (err) {
    console.warn("[CMP] handleConsent error:", err);
  }
}

// ── Banner & Preferences ─────────────────────────────────────────

/** Re-open banner/preferences after consent (privacy widget). No page lock; saves again on submit. */
function openConsentSettings(): void {
  if (!config) return;
  removeConsentWidget();
  setStrictPageBlock(false);
  releasePageLockIfIdle();
  removeBanner();
  closePreferences();
  showBanner({ blockPage: false });
}

function showBanner(options?: BannerOptions): void {
  try {
    if (!config) return;
    removeConsentWidget();

    const themeJson = config.themeJson as Record<string, unknown> || {};
    const compliance = (themeJson.compliance || {}) as Record<string, unknown>;
    const isCCPA = compliance.ccpaEnabled === true;

    const categories = resolveCategories(config);
    const blockPage = shouldBlockPageUntilConsent(config.themeJson);
    const bannerOpts: BannerOptions = {
      ...(options || {}),
      blockPage: options?.blockPage ?? blockPage,
    };
    renderBanner(
    config,
    () => {
      const consent = buildAcceptAllConsent(categories);
      if (config?.cookies?.length) {
        consent.cookies = Object.fromEntries(config.cookies.map((cookie) => [cookie.key, true]));
      }
      handleConsent(consent, "accept_all");
    },
    () => {
      const consent = buildRejectAllConsent(categories);
      if (config?.cookies?.length) {
        consent.cookies = Object.fromEntries(
          config.cookies.map((cookie) => [cookie.key, consent.categories[cookie.category] ?? false])
        );
      }
      handleConsent(consent, "reject_all");
    },
    () => {
      showPreferencesModal({ lockPage: bannerOpts.blockPage });
    },
    isCCPA ? async () => {
      try {
        await CMP.optOut();
        removeBanner();
      } catch (e) {
        console.warn("[CMP] optOut error:", e);
      }
    } : undefined,
    bannerOpts
  );
  } catch (err) {
    console.warn("[CMP] showBanner error:", err);
  }
}

/**
 * Başarılı üyelik köprüsünden sonra: mevcut çerezler rızayla uyumsuzsa rızayı sıfırlar ve
 * yerelleştirilmiş login uyarısı ile banner gösterir (tema admin config’ten gelir).
 */
function runPostLoginConsentBridge(): void {
  try {
    const cfg = config ?? getCachedConfig();
    if (!cfg) return;

    const stored = getConsent();
    if (!stored) return;

    if (!detectPostLoginCookieMismatch(stored, cfg.cookies, cfg.categories)) return;

    const rejected = buildRejectAllConsent(cfg.categories, cfg.cookies);
    setConsent(rejected);
    updateConsentMode(rejected.categories);
    clearDeniedCookies();
    const deniedCats = Object.entries(rejected.categories)
      .filter(([, v]) => !v)
      .map(([k]) => k);
    clearDeniedLocalStorage(deniedCats);
    clearConsent();
    closePreferences();
    removeBanner();
    showBanner({ reconsentNotice: resolveLoginReconsentNotice(cfg) });
  } catch (e) {
    console.debug("[CMP] post-login consent bridge:", e);
  }
}

function showPreferencesModal(options?: { lockPage?: boolean }): void {
  try {
    if (!config) return;
    const categories = resolveCategories(config);
    const current = getConsent() || buildDefaultConsent(categories, config.cookies);
    const lockPage = options?.lockPage ?? shouldBlockPageUntilConsent(config.themeJson);

    renderPreferences(
    config,
    current,
    (consent) => {
      removeBanner();
      handleConsent(consent, "partial");
    },
    () => {
      syncConsentWidget(config);
    },
    { lockPage }
  );
  } catch (err) {
    console.warn("[CMP] showPreferencesModal error:", err);
  }
}

// ── Public API ───────────────────────────────────────────────────

/**
 * Public CMP API exposed on window.CMP
 */
export const CMP = {
  /**
   * Get current consent state.
   */
  getConsent(): ConsentState | null {
    try { return getConsent(); } catch (e) { console.warn("[CMP] getConsent error:", e); return null; }
  },

  /**
   * Programmatically set consent.
   * Triggers script activation for newly allowed categories.
   */
  setConsent(consent: ConsentState): void {
    try { handleConsent(consent, "partial"); } catch (e) { console.warn("[CMP] setConsent error:", e); }
  },

  /**
   * Show the consent banner again (e.g. from a "Cookie Settings" link).
   */
  showBanner(): void {
    try { removeBanner(); showBanner(); } catch (e) { console.warn("[CMP] showBanner error:", e); }
  },

  /**
   * Show the preferences modal.
   * When user clicks "Save", newly allowed scripts run immediately
   * without page reload.
   */
  showPreferences(): void {
    try { showPreferencesModal(); } catch (e) { console.warn("[CMP] showPreferences error:", e); }
  },

  /**
   * Withdraw all consent. Resets to necessary-only.
   * Clears denied cookies/localStorage, sends "withdraw" to consent_records (sealed).
   * Already-executed scripts cannot be stopped; page reload is recommended.
   */
  withdrawConsent(): void {
    try {
      if (!config) return;
      const consent = buildRejectAllConsent(config.categories, config.cookies);
      setConsent(consent);
      clearDeniedCookies();
      const deniedCategories = Object.entries(consent.categories)
        .filter(([, v]) => !v)
        .map(([k]) => k);
      clearDeniedLocalStorage(deniedCategories);
      clearConsent();
      updateConsentMode(consent.categories);
      void sendConsentEvent("withdraw", consent);
      emit("cmp:consentChanged", consent);
      console.info("[CMP] Consent withdrawn. Page reload recommended for full effect.");
    } catch (e) { console.warn("[CMP] withdrawConsent error:", e); }
  },

  /**
   * Re-open banner/preferences. Use from footer link or "Çerez Tercihlerimi Yönet".
   * Shows banner with current selections; user can click "Tercihleri Yönet" for modal.
   */
  showSettings(): void {
    try { openConsentSettings(); } catch (e) { console.warn("[CMP] showSettings error:", e); }
  },

  /**
   * Subscribe to CMP events.
   * Events: "cmp:ready", "cmp:consentChanged"
   */
  on(event: CmpEventType, callback: EventCallback): void {
    try {
      if (!listeners[event]) listeners[event] = [];
      listeners[event].push(callback);
    } catch (e) { console.warn("[CMP] on error:", e); }
  },

  /**
   * Get current script blocking stats.
   */
  getBlockingStats(): { blocked: number; activated: number } {
    try { return getBlockingStats(); } catch (e) { console.warn("[CMP] getBlockingStats error:", e); return { blocked: 0, activated: 0 }; }
  },

  /**
   * CCPA Opt-Out: Do Not Sell or Share My Personal Information
   * Blocks marketing and analytics cookies, sends opt-out event to backend
   */
  async optOut(): Promise<void> {
    try {
      if (!config) {
        console.warn("[CMP] Cannot opt-out: SDK not initialized");
        return;
      }

      const consent = buildOptOutConsent(config.categories, config.cookies);
      setConsent(consent);
      updateConsentMode(consent.categories);
      clearDeniedCookies();

      const apiBase = (window as unknown as { __CMP_API_BASE__?: string }).__CMP_API_BASE__ || "";
      await fetch(`${apiBase}/api/consent/opt-out`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          hostname: window.location.hostname,
          visitorId: getUserKey(),
        }),
      });
      console.info("[CMP] Opt-out request processed successfully");
      emit("cmp:consentChanged", consent);
    } catch (err) {
      console.warn("[CMP] optOut error:", err);
    }
  },

  /**
   * Müşteri sitesi: login sonrası yerel visitor id ile harici üye id’sini CMP’de eşler (Rıza Köprüsü).
   * Başarılı linkte çerez/rıza uyumsuzluğu varsa yerelleştirilmiş banner açılır.
   * @see linkVisitorToUser
   */
  linkVisitorToUser,

  /**
   * Tarayıcıdaki çerezlerin mevcut rızayla (ve CMP kataloğuyla) uyumsuz olup olmadığını döner.
   * Sadece okuma; banner açmak için `linkVisitorToUser` köprüsünü veya manuel akışı kullanın.
   */
  detectNewCookiesAfterLogin(): boolean {
    try {
      const cfg = config ?? getCachedConfig();
      if (!cfg) return false;
      const stored = getConsent();
      if (!stored) return false;
      return detectPostLoginCookieMismatch(stored, cfg.cookies, cfg.categories);
    } catch {
      return false;
    }
  },
};

// ── Initialization ───────────────────────────────────────────────

/**
 * Initialize the CMP SDK.
 *
 * Flow:
 *   1. Set Google Consent Mode defaults (all denied)
 *   2. Fetch config from API (/cmp/config)
 *   3. Check policy_version: if server version > stored version → invalidate consent (re-consent)
 *   4. Check banner_version: if changed → just update stored version (no re-consent needed)
 *   5. If consent exists and valid → activate allowed scripts + update GCM
 *   6. If no consent or invalidated → show banner
 *   7. Emit "cmp:ready"
 */
export async function init(apiBase?: string, hostnameOverride?: string): Promise<void> {
  if (apiBase) {
    (window as unknown as { __CMP_API_BASE__: string }).__CMP_API_BASE__ = apiBase;
  }

  setDefaultDenied();

  try {
    config = await fetchConfig(apiBase, getUserLang(), hostnameOverride);
  } catch (err) {
    console.error("[CMP] Failed to initialize:", err);
    return;
  }

  const existing = getConsent();
  let consentValid = !!existing;
  let isReconsent = false;

  // ── Policy version check: invalidate consent if policy changed ──
  if (existing) {
    const storedPolicyVer = getStoredPolicyVersion();
    if (storedPolicyVer !== null && config.policyVersion > storedPolicyVer) {
      console.info(
        `[CMP] Policy version changed (${storedPolicyVer} → ${config.policyVersion}). ` +
        "Invalidating existing consent — re-consent required."
      );
      clearConsent();
      consentValid = false;
      isReconsent = true;
    }
  }

  // ── Banner version check: no re-consent, just note the update ──
  const storedBannerVer = getStoredBannerVersion();
  if (storedBannerVer !== null && config.bannerVersion > storedBannerVer) {
    console.info(
      `[CMP] Banner version changed (${storedBannerVer} → ${config.bannerVersion}). ` +
      "New design will be shown on next banner display."
    );
  }

  // Always persist the latest versions from the server
  savePolicyVersion(config.policyVersion);
  saveBannerVersion(config.bannerVersion);

  if (consentValid && existing) {
    updateConsentMode(existing.categories);
    const allowed = Object.entries(existing.categories)
      .filter(([, v]) => v)
      .map(([k]) => k);
    await activateScripts(allowed);
    clearDeniedCookies();

    const stats = getBlockingStats();
    console.info(`[CMP] Existing consent applied (policy v${config.policyVersion}). ${stats.activated} script(s) activated, ${stats.blocked} still blocked.`);
    syncConsentWidget(config);
  } else {
    const isTr = getUserLang() === "tr";
    const bannerOpts: BannerOptions | undefined = isReconsent
      ? {
          reconsentNotice: isTr
            ? "Çerez politikamız güncellendiği için tercihlerinizi yenilemenizi rica ediyoruz."
            : "Our cookie policy has been updated. Please review and update your preferences.",
        }
      : undefined;
    showBanner(bannerOpts);
    const stats = getBlockingStats();
    console.info(`[CMP] ${isReconsent ? "Consent invalidated (re-consent)" : "No prior consent"}. ${stats.blocked} script(s) blocked, waiting for consent.`);
  }

  installCookieWriteGuard(config.cookies, () => getConsent());

  setConsentWidgetOpenHandler(openConsentSettings);
  emit("cmp:ready", config);
}
